import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { upperFirst } from 'lodash';
import { Typography, Theme, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { disablePaymentCard } from 'common/api/db/functions';
import { changePrimaryPaymentCard } from 'common/api/db/functions';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import { fetchAdyenPaymentMethods } from 'actions/ShopActions';
import { GetPaymentMethods } from 'common/types';
import { BILLING_CURRENCY } from 'common/modules/billing';
import Tag from 'components/Tag';
interface OwnProps {
	items: ICheckout.StoredPaymentMethod[];
}

const SavedCards = (props: OwnProps) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const savedCardInformation = useSelector(ShopSelectors.shopPaymentCards);
	const shopCountry = useSelector(ShopSelectors.activeShopCountry);

	const [isLoading, setIsLoading] = useState(false);
	const { items } = props;

	const primaryCardToken = savedCardInformation?.find((c) => c.primary === true)?.cardToken;

	const getAdyenPaymentMethods = () => {
		const requestData: GetPaymentMethods = {
			shopId: shopId ?? '',
			amount: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			...(shopCountry && { countryCode: shopCountry.toUpperCase() }),
			shopperReference: shopId,
		};
		dispatch(fetchAdyenPaymentMethods(requestData));
	};

	const setAsPrimary = async (cardToken: string | undefined) => {
		setIsLoading(true);
		!!cardToken &&
			(await changePrimaryPaymentCard({
				shopId,
				cardToken,
			}));
		setIsLoading(false);
	};

	const disableCard = async (cardId: string | undefined) => {
		if (!cardId) return;
		setIsLoading(true);
		const savedCardInfo = savedCardInformation?.find((s) => s.cardToken === cardId);

		if (savedCardInfo) {
			const data = {
				shopId,
				shopperReference: savedCardInfo.shopperReference,
				recurringDetailReference: savedCardInfo.cardToken,
			};
			const result = await disablePaymentCard(data);

			if (result.response === '[detail-successfully-disabled]') {
				getAdyenPaymentMethods();
			}
		}
		setIsLoading(false);
	};

	const renderPaymentCard = (
		{ name, expiryMonth, expiryYear, lastFour, id }: Partial<ICheckout.StoredPaymentMethod>,
		isPrimary: boolean,
	) => (
		<div className={classes.cardDetailsContainer} key={id}>
			<div className={classes.cardDetailsText}>
				<Typography variant="h6" className={classes.cardNumber}>
					{upperFirst(name)} {t('shop.billingView.paymentCards.endingIn', 'ending in')} {lastFour}
				</Typography>
				<Typography color="secondary">
					{t('shop.billingView.paymentCards.expires', 'Expires')} {expiryMonth}/{expiryYear}
				</Typography>
			</div>
			<div className={classes.cardActionsBlock}>
				{isPrimary ? (
					<Tag text="Default" color="success" />
				) : (
					<Button
						color="secondary"
						size="small"
						onClick={() => setAsPrimary(id)}
						disabled={isLoading}
						data-qa="PlanBilling--makeDefault"
					>
						{isLoading
							? t('shop.billingView.paymentCards.loadingDefault', 'loading...')
							: t('shop.billingView.paymentCards.makeAsDefault', 'Make as default')}
					</Button>
				)}
				<Button
					color="secondary"
					size="small"
					disabled={isPrimary || isLoading}
					onClick={() => disableCard(id)}
					data-qa="PlanBilling--removeCard"
				>
					{t('shop.billingView.paymentCards.removeCard', 'Remove')}
				</Button>
			</div>
		</div>
	);

	return (
		<div className={classes.savedCardsContainer}>
			{items.map((m) => renderPaymentCard(m, m.id === primaryCardToken))}
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		savedCardsContainer: {
			marginTop: theme.spacing(4),
		},
		cardDetailsContainer: {
			marginBottom: theme.spacing(3),
		},
		cardActionsBlock: {
			display: 'inline-block',
			float: 'right',
			[theme.breakpoints.down('sm')]: {
				float: 'none',
				marginTop: theme.spacing(1),
			},
		},
		cardDetailsText: {
			display: 'inline-block',
		},
		cardNumber: {
			fontSize: '1.8rem',
			display: 'inline-block',
		},
	}),
);

export default SavedCards;
